import { lazy, ComponentType } from 'react';

// Retry wrapper for lazy imports to handle transient network failures
const retryImport = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  delay = 500
): Promise<{ default: T }> => {
  return importFn().catch((error) => {
    if (retries > 0) {
      return new Promise<{ default: T }>((resolve) =>
        setTimeout(() => resolve(retryImport(importFn, retries - 1, delay * 1.5)), delay)
      );
    }
    throw error;
  });
};

// Lazy load all page components for better performance with retry logic
export const ROUTE_COMPONENTS = {
  ActivityDetailPage: lazy(() => retryImport(() => import("../pages/ActivityDetailPage"))),
  TaskDetailPage: lazy(() => retryImport(() => import("../pages/TaskDetailPage"))),

  DISCTestPage: lazy(() => retryImport(() => import("../pages/DISCTest"))),
  
  LogActivityPage: lazy(() => retryImport(() => import("../pages/LogActivityPage"))),
  ActivityFeedPage: lazy(() => retryImport(() => import("../pages/ActivityFeedPage"))),
  NotesTasksPage: lazy(() => retryImport(() => import("../pages/NotesTasksPage"))),
  PetPage: lazy(() => retryImport(() => import("../pages/PetPage"))),
  ClubPage: lazy(() => retryImport(() => import("../pages/ClubPage"))),
  AchievementsPage: lazy(() => retryImport(() => import("../pages/AchievementsPage"))),
  
  RedemptionPage: lazy(() => retryImport(() => import("../pages/RedemptionPage"))),
  AdminPage: lazy(() => retryImport(() => import("../pages/AdminPage"))),
  TrackerAdminPage: lazy(() => retryImport(() => import("../pages/TrackerAdminPage"))),
  SalesResourcesPage: lazy(() => retryImport(() => import("../pages/SalesResourcesPage"))),
  ManagerToolsPage: lazy(() => retryImport(() => import("../pages/ManagerToolsPage"))),
  ConsultantToolsPage: lazy(() => retryImport(() => import("../pages/ConsultantToolsPage"))),
  AiaLinksPage: lazy(() => retryImport(() => import("../pages/AiaLinksPage"))),
  ResourcesHubPage: lazy(() => retryImport(() => import("../pages/ResourcesHubPage"))),
  
  WeeklyReflectionsPage: lazy(() => retryImport(() => import("../pages/WeeklyReflectionsPage"))),
  UserActivityBreakdownPage: lazy(() => retryImport(() => import("../pages/UserActivityBreakdownPage"))),
  Feedback: lazy(() => retryImport(() => import("../pages/Feedback"))),
  InsuranceCalculatorPage: lazy(() => retryImport(() => import("../pages/InsuranceCalculatorPage"))),
  PledgeCalculatorPage: lazy(() => retryImport(() => import("../pages/PledgeCalculatorPage"))),
  ActivityCalculator: lazy(() => retryImport(() => import("../pages/ActivityCalculator"))),
  CoachingPage: lazy(() => retryImport(() => import("../pages/CoachingPageRefactored"))),
  CoachingSettings: lazy(() => retryImport(() => import("../pages/CoachingSettingsRefactored"))),
  BookingConfirmation: lazy(() => retryImport(() => import("../pages/BookingConfirmation"))),
  MegaAppHome: lazy(() => retryImport(() => import("../components/mega-app/MegaAppHome"))),
  PrivacyPolicyPage: lazy(() => retryImport(() => import("../pages/PrivacyPolicyPage"))),
  CommissionCalculatorPage: lazy(() => retryImport(() => import("../pages/CommissionCalculatorPage"))),
  ManagerCalculator: lazy(() => retryImport(() => import("../pages/ManagerCalculator"))),
  HabitsPage: lazy(() => retryImport(() => import("../pages/HabitsPage"))),
  WheelOfFortunePage: lazy(() => retryImport(() => import("../pages/WheelOfFortunePage"))),
  
  TermsOfServicePage: lazy(() => retryImport(() => import("../pages/TermsOfServicePage"))),
  WDollarUsersPage: lazy(() => retryImport(() => import("../pages/WDollarUsersPage"))),
  WinCreditsPage: lazy(() => retryImport(() => import("../pages/WinCreditsPage"))),
  WDollarOverviewPage: lazy(() => retryImport(() => import("../pages/WDollarOverviewPage"))),
  WDollarCreditHistoryPage: lazy(() => retryImport(() => import("../pages/WDollarCreditHistoryPage"))),
  UserWDollarDetailPage: lazy(() => retryImport(() => import("../pages/UserWDollarDetailPage"))),
  WDollarOpsUsersPage: lazy(() => retryImport(() => import("../pages/WDollarOpsUsersPage"))),
  WDollarOpsSettingsPage: lazy(() => retryImport(() => import("../pages/WDollarOpsSettingsPage"))),
  SalesToolsPage: lazy(() => retryImport(() => import("../pages/SalesToolsPage"))),
} as const;

export interface RouteConfig {
  path: string;
  element?: keyof typeof ROUTE_COMPONENTS;
  protected?: boolean;
  redirectTo?: string;
}

export const ROUTE_CONFIGS: RouteConfig[] = [
  // New URL structure
  { path: "/tracker/log", element: "LogActivityPage", protected: true },
  { path: "/tracker/feed", element: "ActivityFeedPage", protected: true },
  { path: "/tracker/pet", element: "PetPage", protected: true },
  { path: "/tracker/redeem", element: "RedemptionPage", protected: true },
  { path: "/tracker/reflections", element: "WeeklyReflectionsPage", protected: true },
  { path: "/tracker/club", element: "ClubPage", protected: true },
  { path: "/tracker/achievements", element: "AchievementsPage", protected: true },
  { path: "/tracker/sales-activity-calc", element: "PledgeCalculatorPage", protected: true },
  { path: "/tracker/wheel", element: "WheelOfFortunePage", protected: true },
  { path: "/habits", element: "HabitsPage", protected: true },
  
  // Tracker Admin routes
  { path: "/tracker/admin/consultants", element: "TrackerAdminPage", protected: true },
  { path: "/tracker/admin/analytics", element: "TrackerAdminPage", protected: true },
  { path: "/tracker/admin/goals", element: "TrackerAdminPage", protected: true },
  { path: "/tracker/admin/rewards", element: "TrackerAdminPage", protected: true },
  
  { path: "/tracker/admin/apps", element: "TrackerAdminPage", protected: true },
  { path: "/tracker/admin/settings", element: "TrackerAdminPage", protected: true },
  { path: "/tracker/admin", redirectTo: "/tracker/admin/consultants" },
  
  { path: "/admin", redirectTo: "/tracker/admin" },
  { path: "/feedback", element: "Feedback", protected: true },
  { path: "/income-calculator", element: "InsuranceCalculatorPage", protected: true },
  { path: "/coaching", element: "CoachingSettings", protected: true },
  
  // User and activity detail pages
  { path: "/tracker/user-breakdown/:userSlug", element: "UserActivityBreakdownPage", protected: true },
  { path: "/activity/:id", element: "ActivityDetailPage", protected: true },
  { path: "/task/:type/:id", element: "TaskDetailPage", protected: true },
  
  // Public routes
  { path: "/coaching/:slug", element: "CoachingPage" },
  { path: "/booking-confirmation/:bookingId", element: "BookingConfirmation" },
  { path: "/privacy-policy", element: "PrivacyPolicyPage" },
  
  // Backward compatibility redirects
  { path: "/dashboard/log", redirectTo: "/tracker/log" },
  { path: "/dashboard/feed", redirectTo: "/tracker/feed" },
  { path: "/dashboard/pet", redirectTo: "/tracker/pet" },
  { path: "/dashboard/redeem", redirectTo: "/tracker/redeem" },
  { path: "/dashboard/stats", redirectTo: "/tracker/stats" },
  { path: "/dashboard/reflections", redirectTo: "/tracker/reflections" },
  { path: "/dashboard/club", redirectTo: "/tracker/club" },
  { path: "/dashboard/pledge-calculator", redirectTo: "/tracker/sales-activity-calc" },
  { path: "/dashboard/admin", redirectTo: "/admin" },
  { path: "/dashboard/feedback", redirectTo: "/feedback" },
  { path: "/dashboard/insurance-calculator", redirectTo: "/income-calculator" },
  { path: "/dashboard/coaching-settings", redirectTo: "/coaching" },
  { path: "/dashboard/user-breakdown/:userSlug", redirectTo: "/tracker/user-breakdown/:userSlug" },
  { path: "/dashboard", redirectTo: "/tracker/log" },
];
